// RFC-0008 T4 + 模板预览扩展：/demo/:templateId — 翻页查看完整模板
//
// 1）从 bundle 直接读 TEMPLATE_SLIDES[templateId]（构建期 inline 的 SVG）
// 2）翻页 + 缩略图布局，UI 复用 deck.tsx 的 viewer 样式
// 3）顶部 CTA「用这个风格做我的 PPT」 → 触发 addReference 回小北 + navigate('/')
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../components/user-provider';
import { getTemplateById, buildTemplateChatInput } from '../lib/templates';
import { TEMPLATE_SLIDES } from '../lib/template-covers';
import { isArtifactInstalled, setChatInput } from '../lib/north-sdk';

const SKILL_NAME = '小北PPT技能';

export function DemoRoute() {
  const { template_id } = useParams<{ template_id: string }>();
  const { user } = useUser();
  const appUserId = user?.userId ?? null;
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  // 运行时真实 skill_id，进页面后台预热，点击时直接读（不阻塞）
  const skillIdRef = useRef<string | null>(null);

  const meta = template_id ? getTemplateById(template_id) : undefined;
  const slides = useMemo(
    () => (template_id ? (TEMPLATE_SLIDES[template_id] ?? []) : []),
    [template_id],
  );

  // 切模板时回到第一页
  useEffect(() => {
    setActiveIdx(0);
  }, [template_id]);

  // 后台预热 skill_id（不阻塞）：点击「用这个风格」时直接读 ref，输入框秒出
  useEffect(() => {
    if (!appUserId) return;
    void isArtifactInstalled(SKILL_NAME).then((r) => {
      if (r.artifactId) skillIdRef.current = r.artifactId;
    });
  }, [appUserId]);

  // 键盘翻页
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (slides.length === 0) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        if (activeIdx < slides.length - 1) {
          e.preventDefault();
          setActiveIdx(activeIdx + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        if (activeIdx > 0) {
          e.preventDefault();
          setActiveIdx(activeIdx - 1);
        }
      } else if (e.key === 'Escape') {
        navigate('/');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length, activeIdx, navigate]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  const handleAdoptStyle = useCallback(async () => {
    if (!appUserId || !meta) return;
    // 直接读后台预热的 skill_id，不阻塞（isArtifactInstalled 在 dev 约 7s）
    const text = buildTemplateChatInput(meta, appUserId, skillIdRef.current);
    const result = await setChatInput(text);
    showToast(result.success ? '已填好引导，补全后发送' : '插入失败 — 请重试');
  }, [appUserId, meta]);

  if (!meta) {
    return (
      <div className="page page--fit">
        <header className="page__header">
          <Link to="/" className="btn btn--ghost">
            ← 返回
          </Link>
        </header>
        <main className="page__body">
          <div className="center-card">
            <h2>找不到这个模板</h2>
            <p>模板 id「{template_id}」不存在。</p>
          </div>
        </main>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="page page--fit">
        <header className="page__header">
          <Link to="/" className="btn btn--ghost">
            ← 返回
          </Link>
        </header>
        <main className="page__body">
          <div className="center-card">
            <h2>{meta.name}</h2>
            <p>模板预览还没有生成，去 monorepo 根跑 sync-template-covers.sh。</p>
          </div>
        </main>
      </div>
    );
  }

  const active = slides[activeIdx];

  return (
    <div className="page page--fit">
      <header className="page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/" className="btn btn--ghost btn--sm">
            ← 返回
          </Link>
          <h1 className="page__title">{meta.name}</h1>
          <span className="template-chip">demo · {slides.length} 页</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            className="btn"
            href={`/template-demos/${meta.id}.pptx`}
            download={`${meta.id}-demo.pptx`}
          >
            下载 demo
          </a>
          <button
            className="btn btn--primary"
            onClick={() => void handleAdoptStyle()}
          >
            用这个风格做我的 PPT
          </button>
        </div>
      </header>

      <main className="page__body">
        <div className="viewer">
          <aside className="viewer__thumbs">
            {slides.map((s, idx) => (
              <button
                key={s.file}
                className={
                  'viewer__thumb' +
                  (idx === activeIdx ? ' viewer__thumb--active' : '')
                }
                onClick={() => setActiveIdx(idx)}
                aria-label={`第 ${idx + 1} 页`}
              >
                <span className="viewer__thumb-num">{idx + 1}</span>
                <div dangerouslySetInnerHTML={{ __html: s.svg }} />
              </button>
            ))}
          </aside>

          <section className="viewer__canvas">
            <div className="viewer__canvas-inner">
              <div dangerouslySetInnerHTML={{ __html: active.svg }} />
            </div>
          </section>
        </div>
      </main>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
